import { axiosInstance } from "api/axios";
import useCustomMutation from "hooks/useCustomMutation";
import useCustomQuery from "hooks/useCustomQuery";

/**
 * Schematy księgowania (dekretacji) kontrahenta — przechowywane w BE
 * (`ContractorPostingSchemes`). Per (kontrahent, kierunek); może istnieć kilka
 * schematów na kierunek, jeden domyślny. Kierunek wg roli kontrahenta na
 * dokumencie: sprzedawca → „Purchase" (zakup), nabywca → „Sale" (sprzedaż).
 * Kontrakt: docs/backend-posting-schemes.md.
 */

export type PostingSchemeDirection = "Purchase" | "Sale";
export type PostingSchemeBase = "Net" | "Vat" | "Gross";
export type PostingSchemeSide = "Dt" | "Ct"; // Wn / Ma
export type PostingSchemeSource = "Auto" | "Manual";

export interface IPostingSchemeLine {
  side: PostingSchemeSide;
  account: string;
  base: PostingSchemeBase;
  factor: number;
  description?: string;
}

export interface IPostingScheme {
  id?: string;
  contractorId: string;
  contractorName?: string;
  direction: PostingSchemeDirection;
  name?: string;
  source?: PostingSchemeSource;
  isDefault?: boolean;
  lines: IPostingSchemeLine[];
}

export interface ISaveSchemeRequest {
  contractorId: string;
  direction: PostingSchemeDirection;
  name?: string;
  /** „add" (nowy schemat) / „update" (aktualizacja istniejącego). */
  mode: "add" | "update";
  schemeId?: string;
  makeDefault?: boolean;
  lines: IPostingSchemeLine[];
}

// BE zwraca PascalCase (PropertyNamingPolicy = null) → camelCase.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapScheme = (raw: any): IPostingScheme => ({
  id: raw?.Id ?? undefined,
  contractorId: raw?.ContractorId ?? "",
  contractorName: raw?.ContractorName ?? undefined,
  direction: (raw?.Direction ?? "Purchase") as PostingSchemeDirection,
  name: raw?.Name ?? undefined,
  source: (raw?.Source ?? undefined) as PostingSchemeSource | undefined,
  isDefault: !!raw?.IsDefault,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  lines: (raw?.Lines ?? []).map((l: any) => ({
    side: (l.Side ?? "Dt") as PostingSchemeSide,
    account: l.Account ?? "",
    base: (l.Base ?? "Net") as PostingSchemeBase,
    factor: typeof l.Factor === "number" ? l.Factor : 1,
    description: l.Description ?? undefined,
  })),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapList = (raw: any): IPostingScheme[] =>
  Array.isArray(raw) ? raw.map(mapScheme) : [];

const toLinesBody = (lines: IPostingSchemeLine[]) =>
  lines.map((l) => ({
    Side: l.side,
    Account: l.account,
    Base: l.base,
    Factor: l.factor,
    Description: l.description,
  }));

const getContractorSchemesApi = async (contractorId: string): Promise<IPostingScheme[]> =>
  mapList((await axiosInstance.get(`/contractors/${contractorId}/posting-schemes`)).data);

const resolveSchemesApi = async (params: {
  nip: string;
  direction: PostingSchemeDirection;
}): Promise<IPostingScheme[]> =>
  mapList(
    (
      await axiosInstance.get("/contractors/posting-schemes/resolve", {
        params: { nip: params.nip, direction: params.direction },
      })
    ).data,
  );

const saveSchemeApi = async (req: ISaveSchemeRequest): Promise<IPostingScheme> =>
  mapScheme(
    (
      await axiosInstance.post(`/contractors/${req.contractorId}/posting-schemes`, {
        Direction: req.direction,
        Name: req.name,
        Mode: req.mode,
        SchemeId: req.schemeId,
        MakeDefault: !!req.makeDefault,
        Lines: toLinesBody(req.lines),
      })
    ).data,
  );

export interface IBackfillResult {
  created: number;
  skipped: number;
  contractors: number;
}

const backfillApi = async (): Promise<IBackfillResult> => {
  const raw = (await axiosInstance.post("/contractors/posting-schemes/backfill")).data;
  return {
    created: raw?.Created ?? 0,
    skipped: raw?.Skipped ?? 0,
    contractors: raw?.Contractors ?? 0,
  };
};

/** Wpis zbiorczego indeksu schematów — (kontrahent, kierunek) który MA schemat. */
export interface ISchemeIndexEntry {
  contractorId: string;
  nip?: string;
  direction: PostingSchemeDirection;
}

const getSchemesIndexApi = async (): Promise<ISchemeIndexEntry[]> => {
  const raw = (await axiosInstance.get("/contractors/posting-schemes/index")).data;
  if (!Array.isArray(raw)) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return raw.map((r: any) => ({
    contractorId: r?.ContractorId ?? "",
    nip: r?.Nip ?? undefined,
    direction: (r?.Direction ?? "Purchase") as PostingSchemeDirection,
  }));
};

export const SCHEMES_QUERY_KEY = "contractor-posting-schemes";
export const SCHEMES_RESOLVE_QUERY_KEY = "contractor-posting-schemes-resolve";
export const SCHEMES_INDEX_QUERY_KEY = "contractor-posting-schemes-index";

const postingSchemeService = {
  useContractorSchemes: (contractorId: string, enabled = true) =>
    useCustomQuery<IPostingScheme[]>(
      [SCHEMES_QUERY_KEY, contractorId],
      () => getContractorSchemesApi(contractorId),
      { enabled: enabled && !!contractorId },
    ),

  /** Schematy kontrahenta po NIP + kierunek — aktywacja/zastosowanie „Dekretuj". */
  useResolveSchemes: (nip: string, direction: PostingSchemeDirection, enabled = true) =>
    useCustomQuery<IPostingScheme[]>(
      [SCHEMES_RESOLVE_QUERY_KEY, nip, direction],
      () => resolveSchemesApi({ nip, direction }),
      { enabled: enabled && !!nip, staleTime: 30_000 },
    ),

  /** Zbiorczy indeks (kontrahent, kierunek)→schemat — do audytu/oznaczania listy. */
  useSchemesIndex: () =>
    useCustomQuery<ISchemeIndexEntry[]>(
      [SCHEMES_INDEX_QUERY_KEY],
      () => getSchemesIndexApi(),
      { staleTime: 60_000 },
    ),

  useSaveScheme: () => useCustomMutation<IPostingScheme, ISaveSchemeRequest>(saveSchemeApi),

  useBackfill: () => useCustomMutation<IBackfillResult, void>(() => backfillApi()),

  saveSchemeApi,
};

export default postingSchemeService;
